import React from 'react';
import { useTranslation } from 'react-i18next';
import { Crown } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FEATURE_LABELS, PLAN_LABELS } from '../../modules/entitlements/constants';
import { usePlan } from '../../modules/entitlements';
import { cn } from '../../lib/utils';

interface UpgradePromptProps {
    // The feature key that triggered this prompt
    featureKey: string;
    // Optional: the minimum plan required (shown in the CTA)
    requiredPlan?: 'STARTER' | 'GROWTH' | 'PRO';
    // Override the default copy
    title?: string;
    description?: string;
    // The feature's own icon (lucide) shown on the card variant's neutral disc.
    // Falls back to the premium crown when omitted.
    icon?: React.ComponentType<{ className?: string }>;
    // Called when the user clicks "Upgrade" — navigate to billing page
    onUpgrade?: () => void;
    // Compact inline variant (no border/padding)
    variant?: 'default' | 'inline' | 'banner';
    className?: string;
}
/** `default` is the crafted premium card; `inline`/`banner` stay compact for dense surfaces. */
export function UpgradePrompt({
    featureKey,
    requiredPlan = 'PRO',
    title,
    description,
    icon: Icon,
    onUpgrade,
    variant = 'default',
    className,
}: UpgradePromptProps) {
    const { t } = useTranslation();
    const { name: currentPlanName } = usePlan();
    const featureLabel = FEATURE_LABELS[featureKey as keyof typeof FEATURE_LABELS] ?? featureKey;
    const requiredPlanLabel = PLAN_LABELS[requiredPlan] ?? requiredPlan;
    const defaultTitle = t('premium_feature_title', {
        feature: featureLabel,
        plan: requiredPlanLabel,
        defaultValue: `${featureLabel} is a ${requiredPlanLabel} feature`,
    });
    const defaultDescription = t('premium_feature_desc', {
        feature: featureLabel,
        plan: currentPlanName,
        required: requiredPlanLabel,
        defaultValue: `Your ${currentPlanName} plan doesn't include ${featureLabel}. Upgrade to ${requiredPlanLabel} to unlock it.`,
    });
    if (variant === 'inline') {
        return (<span className={cn('inline-flex items-center gap-2 text-sm text-muted-foreground', className)}>
        <Badge variant="outline">{requiredPlanLabel}</Badge>
        {title ?? defaultTitle}
        {onUpgrade && (<Button variant="link" size="sm" className="h-auto p-0" onClick={onUpgrade}>{t("upgrade")}</Button>)}
      </span>);
    }
    if (variant === 'banner') {
        return (<div className={cn('flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3', className)}>
        <div className="flex items-center gap-3">
          <Badge variant="outline">
            {requiredPlanLabel}
          </Badge>
          <span className="text-sm font-medium">{title ?? defaultTitle}</span>
        </div>
        {onUpgrade && (<Button size="sm" onClick={onUpgrade}>{t("upgrade")}</Button>)}
      </div>);
    }
    // default: crafted premium card
    return (<div className={cn('relative flex w-full max-w-md flex-col items-center gap-5 overflow-hidden rounded-lg border border-border bg-card px-8 py-9 text-center shadow-sm', 'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-300', className)}>
      {/* The feature's own glyph on a neutral disc (the crown when none is given). */}
      <span aria-hidden className="relative flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {Icon ? <Icon className="size-[18px]"/> : <Crown className="size-[18px]"/>}
      </span>

      <div className="space-y-1.5">
        <p className="text-balance text-lg font-semibold text-foreground">{title ?? defaultTitle}</p>
        <p className="mx-auto max-w-sm text-pretty text-sm leading-relaxed text-muted-foreground">
          {description ?? defaultDescription}
        </p>
      </div>

      {onUpgrade && (<Button onClick={onUpgrade} className="min-w-44">
        {t("upgrade_to")} {requiredPlanLabel}
      </Button>)}
    </div>);
}
